import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchLevelListAPI, fetchStudentsAPI } from "../student/studentAPI";
import { fetchAssignmentListAPI } from "../worksheets/worksheetAPI";
import { fetchAllUserAPI } from "./adminAPI";

const initialState = {
  users: [],
  selectedUser: {
    id: null,
    nickname: "유저를 선택해주세요.",
  },
  levels: [],
  allStudents: [],
  filteredStudents: [],
  results: [],
  current_page: 1,
  total_pages: 1,
  error: null,
};

// 1) 반 리스트 조회
export const fetchLevels = createAsyncThunk(
  "admin/fetchLevels",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userId = state.admin.selectedUser.id;
      const data = await fetchLevelListAPI(userId);
      return data; // 배열 형태 또는 {id, name}[]
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStudents = createAsyncThunk(
  "admin/fetchStudents",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userId = state.admin.selectedUser.id;
      const data = await fetchStudentsAPI(userId);
      return data; // 배열 형태 또는 {id, name}[]
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAssignments = createAsyncThunk(
  "admin/fetchAssignments",
  async (
    {
      page = 1,
      limit = 13,
      type = null,
      isFinished = null,
      keyword = null,
      levelId = null,
      studentId = null,
      studentGrade = null,
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const userId = state.admin.selectedUser.id;
      const data = await fetchAssignmentListAPI(
        page,
        limit,
        type,
        isFinished,
        levelId,
        keyword,
        studentId,
        studentGrade,
        userId
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  "admin/fetchAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchAllUserAPI();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { users } = getState().admin;
      if (users.length > 0) {
        return false;
      }
    },
  }
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    filterStudentsByKeyword: (state, action) => {
      const keyword = action.payload.toLowerCase();
      if (keyword.trim() === "") {
        state.filteredStudents = state.allStudents;
      } else {
        state.filteredStudents = state.allStudents.filter((student) =>
          student.name.toLowerCase().includes(keyword)
        );
      }
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLevels
      .addCase(fetchLevels.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchLevels.fulfilled, (state, action) => {
        state.levels = action.payload;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.error = action.payload;
      })

      // fetchStudents
      .addCase(fetchStudents.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.allStudents = action.payload;
        state.filteredStudents = action.payload;
      })

      .addCase(fetchAssignments.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        const { results, current_page, total_pages } = action.payload;
        state.results = results;
        state.current_page = current_page;
        state.total_pages = total_pages;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch worksheets";
      })

      .addCase(fetchAllUsers.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { filterStudentsByKeyword, setSelectedUser } = adminSlice.actions;
export default adminSlice.reducer;
